'use client'

import { AddIcon } from '@repo/ui/AddIcon'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useThirdPartyFormWizardStore } from '@repo/store/useThirdPartyFormWizardStore'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIListItemIcon from '@mui/material/ListItemIcon'
import MUIListItemText from '@mui/material/ListItemText'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIStack from '@mui/material/Stack'
import RefreshIcon from '@mui/icons-material/Refresh'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'

type CustomToolbarProps = {
  onRefresh?: () => void
  onColumnsClick?: () => void
}

export const CustomToolbar = ({ onRefresh, onColumnsClick }: CustomToolbarProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const { currentMatter } = useMattersStore()
  const { initialize } = useThirdPartyFormWizardStore()

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNewForm = () => {
    initialize({ mode: 'create', selectedMatter: currentMatter })
    navigate('?wizard=ThirdPartyForm&stage=add-form&step=select-options')
    handleClose()
  }

  const handleColumns = () => {
    handleClose()
    onColumnsClick?.()
  }

  return (
    <MUIBox sx={{ display: 'flex', justifyContent: 'flex-end', padding: '0.5rem 1rem' }}>
      <MUIStack direction="row" gap={1.5} alignItems="center">
        <MUIIconButton
          data-testid="ThirdPartyFormsRefreshButton"
          size="small"
          aria-label="Refresh"
          onClick={onRefresh}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '4px' }}
        >
          <RefreshIcon fontSize="small" />
        </MUIIconButton>
        <MUIButton
          data-testid="ThirdPartyFormsActionsButton"
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          aria-haspopup="true"
          aria-controls={open ? 'third-party-forms-actions-menu' : undefined}
          aria-expanded={open || undefined}
          onClick={handleActionsClick}
        >
          Actions
        </MUIButton>
        <MUIMenu
          id="third-party-forms-actions-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { width: 220, mt: 1 } } }}
        >
          <MUIMenuItem data-testid="ThirdPartyFormsNewFormMenuItem" onClick={handleNewForm} sx={{ height: 46 }}>
            <MUIListItemIcon>
              <AddIcon sx={{ height: 22, width: 22 }} />
            </MUIListItemIcon>
            <MUIListItemText primary="New form" sx={{ '& .MuiTypography-root': { mb: 0 } }} />
          </MUIMenuItem>
          <MUIMenuItem data-testid="ThirdPartyFormsColumnsMenuItem" onClick={handleColumns} sx={{ height: 46 }}>
            <MUIListItemIcon>
              <ViewColumnIcon sx={{ color: 'text.secondary' }} />
            </MUIListItemIcon>
            <MUIListItemText primary="Columns" sx={{ '& .MuiTypography-root': { mb: 0 } }} />
          </MUIMenuItem>
        </MUIMenu>
      </MUIStack>
    </MUIBox>
  )
}
